import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import _ from 'lodash';
import dayjs from 'dayjs';

import { CoverDataItem, CustomLPTReportItem } from '../../../utils/models';
import { getAffirmativeOptions, getCustomLPTColumns, getLPTModalData } from '../../../utils/data';

import AutTable from '../../blocks/aut-grid-table/index';
import EditLPTReportItemForm from '../custom-lpt/editLPTReportItemForm';
import Form from '../../common/form';
import LPTReportItemForm from '../custom-lpt/lptReportItemForm';

import toastService from '../../../services/toastService';
import { MATERIAL_OPTIONS } from '../../../utils/constants';

class MPIReportForm extends Form {
	state = {
		data: {
			bgContrastMediumUsed: '',
			surfaceCondition: '',
			surfaceTemperature: '',
			magneticMediumUsed: '',
			equipmentCalibrationDueDate: new Date(),
			calibrationDueDate: new Date(),
			dateOfInspection: new Date(),
			blackInkBatchNumber: '',
			blackInkExpiryDate: new Date(),
			whiteContrastBatchNumber: '',
			whiteContrastExpiryDate: new Date(),
			modal: '',
			currentUsed: '',
			serialNumber: '',
			material: '',
			method: '',
			pieGuageUsed: '',
			liftingForceCheck: '',
			stageOfTest: '',
			equipment: '',
			equipmentSerialNumber: '',
			columnUsed: '',
			blackInkUsed: '',
			light: '',
			technique: '',
			data: [],
		},
		errors: {},
		loading: false,
		progress: 0,
		reportItemToEdit: null,
	};

	schema = {
		surfaceCondition: Joi.string().min(3).required().label('Surface Condition'),
		stageOfTest: Joi.string().min(3).required().label('Stage of Test'),
		equipment: Joi.string().min(3).required().label('MPI Equipment'),
		equipmentSerialNumber: Joi.string().min(3).required().label('MPI Equipment Serial Number'),
		equipmentCalibrationDueDate: Joi.date().greater('now').required().label('Equipment Cal. Due Date'),
		calibrationDueDate: Joi.date().greater('now').required().label('Light Meter Cal. Due Date'),
		dateOfInspection: Joi.date().less('now').required().label('Date of Inspection'),
		blackInkBatchNumber: Joi.string().min(3).required().label('Black Ink Batch Number'),
		blackInkExpiryDate: Joi.date().required().label('Black Ink Expiry Date'),
		whiteContrastBatchNumber: Joi.string().min(3).required().label('White Contrast Batch Number'),
		whiteContrastExpiryDate: Joi.date().required().label('White Contrast Expiry Date'),
		blackInkUsed: Joi.string().min(3).required().label('Black Ink Used'),
		columnUsed: Joi.string().min(3).required().label('Column Used'),
		modal: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			serialNumber: Joi.number().optional(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Lux Meter Model'),
		light: Joi.string().min(3).required().label('Light Intensity'),
		method: Joi.string().min(3).required().label('Method'),
		pieGuageUsed: Joi.string().valid('yes', 'no').required().label('Pie Guage Used'),
		liftingForceCheck: Joi.string().required().label('Lifting Force Check'),
		currentUsed: Joi.string().min(3).required().label('Current Used'),
		serialNumber: Joi.string().min(3).required().label('Lux Meter Serial Number'),
		bgContrastMediumUsed: Joi.string()
			.min(3)
			.required()
			.label('BG Contrast Medium Used'),
		magneticMediumUsed: Joi.string()
			.min(3)
			.required()
			.label('Magnetic Medium Used'),
		surfaceTemperature: Joi.string()
			.min(3)
			.required()
			.label('Surface Temperature'),
		technique: Joi.string().min(3).required().label('Technique'),
		material: Joi.string().required().label('Material'),
		data: Joi.array()
			.min(0)
			.items(
				Joi.object({
					joint: Joi.string().min(3).required().label('Joint/Job Number'),
					observation: Joi.string().min(10).required().label('Observation'),
					result: Joi.string().min(3).required().label('Result'),
					images: Joi.array().required().label('Image'),
				})
			)
			.required()
			.label('Inspection Items'),
	};

	static propTypes = {
		photoNumbers: PropTypes.array,
		initialMPTData: PropTypes.object,
		onAdd: PropTypes.func,
	};

	componentDidMount() {
		if (this.props.initialMPTData) {
			const initialData = { ...this.props.initialMPTData } as typeof this.state.data;

			if (_.isString(initialData.modal)) {
				const optionMatch = getLPTModalData().find((option) => option.value === initialData.modal);
				if (optionMatch) initialData.modal = optionMatch as any;
				else initialData.modal = { label: initialData.modal, value: initialData.modal } as any;
			}

			this.setState({ data: _.omit(initialData, ['refStd', 'acceptanceStd', 'brandName', 'summary']) });
		}
	}

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
		if (prevState.data.equipment !== this.state.data.equipment) {
			const currentUsed = this.getCurrentUsedFromOptions();
			if (!currentUsed) return;

			this.setState({ data: { ...this.state.data, currentUsed } });
		}

		const modal = this.state.data.modal as unknown as CoverDataItem;
		if (modal && prevState.data.modal?.value !== modal.value) {
			const optionMatch = getLPTModalData().find((option) => option.value === modal.value);
			if (optionMatch) this.setState((prev) => ({ ...prev, data: { ...prev.data, serialNumber: optionMatch.serialNumber.toString() }}));
		}
	}

	onAddItem = (newItem: CustomLPTReportItem) => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;

		const match: any = reportItems.find(({ joint }) => joint === newItem.joint);
		if (match) toastService.error(`Entry for ${match.joint} already exists!`);

		this.setState({
			data: { ...this.state.data, data: [...this.state.data.data, newItem] },
		});
	};

	onDeleteItem = (item: CustomLPTReportItem): void => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;

		const newMPTItems = reportItems.filter(({ joint }) => joint !== item.joint);
		this.setState({ data: { ...this.state.data, data: newMPTItems } });
	};

	onEditItem = (item: CustomLPTReportItem): void => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;
		const newMPTItems = reportItems.map((reportItem) => reportItem.joint === item.joint ? item : reportItem);

		this.setState({
			data: { ...this.state.data, data: newMPTItems },
			reportItemToEdit: null,
		} as any);
	};

	onSelectEditItem = (item: CustomLPTReportItem): void => {
		this.setState({ reportItemToEdit: item } as any);
	};

	onCancelEdit = () => {
		this.setState({ reportItemToEdit: null } as any);
	};

	getCurrentUsedFromOptions = () => {
		const optionMatch = EQUIPMENT_OPTIONS.find(({ id }) => id === this.state.data.equipment);
		if (optionMatch) return optionMatch.currentUsed;

		return null;
	};

	getApiData = () => {
		const modal = this.state.data.modal as unknown as CoverDataItem;
		return { ...this.state.data, modal: modal.value };
	};

	doSubmit = (): void => {
		this.props.onAdd(this.state.data);
		toastService.success('Saved successfully!');
	};

	render() {
		return (
			<React.Fragment>
				{this.state.reportItemToEdit ? (
					<EditLPTReportItemForm
						initialData={this.state.reportItemToEdit}
						onAdd={this.onAddItem}
						photoNumbers={this.props.photoNumbers}
						onEdit={this.onEditItem}
						onCancelEdit={this.onCancelEdit}
					/>
				) : (
					<LPTReportItemForm
						onAdd={this.onAddItem}
						photoNumbers={this.props.photoNumbers}
					/>
				)}

				<div className="divider my-4"></div>

				<AutTable
					tableColumns={getCustomLPTColumns()}
					data={this.state.data.data}
					onDeleteItem={this.onDeleteItem}
					onEditItem={this.onSelectEditItem}
				/>

				<div className="divider my-4"></div>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<div className="row">
						<div className="col">
							{this.renderSelect({
								options: SURFACE_CONDITIONS,
								name: 'surfaceCondition',
								label: 'Surface Condition',
								placeholder: 'Enter Surface Condition',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'stageOfTest',
								label: 'Stage of Test',
								placeholder: 'Enter Stage of Test',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'bgContrastMediumUsed',
								label: 'BG Contrast Medium Used',
								placeholder: 'Enter BG Contrast Medium Used',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'light',
								label: 'Light Intensity',
								placeholder: 'Enter Light Intensity Value',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'method',
								label: 'Method',
								placeholder: 'Enter Inspection Method',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'columnUsed',
								label: 'Column Used',
								placeholder: 'Enter Column Used',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'blackInkUsed',
								label: 'Black Ink Used',
								placeholder: 'Enter Black Ink Used',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								name: 'equipment',
								label: 'MPI Equipment',
								placeholder: 'Select MPI Equipment',
								options: EQUIPMENT_OPTIONS,
							})}
						</div>
						
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'equipmentSerialNumber',
								label: 'MPI Equipment Serial Number',
								placeholder: 'Enter MPI Equipment Serial Number',
							})}
						</div>
						
						<div className="col">
							{this.renderDatePicker({
								name: 'equipmentCalibrationDueDate',
								label: 'Equipment Cal. Due Date',
								placeholderText: 'Select Equipment Cal. Due Date',
								minDate: new Date(),
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'dateOfInspection',
								label: 'Date of Inspection',
								placeholderText: 'Select Date of Inspection',
								maxDate: new Date(),
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'calibrationDueDate',
								label: 'Light Meter Cal. Due Date',
								placeholderText: 'Select Light Meter Cal. Due Date',
								minDate: new Date(),
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'magneticMediumUsed',
								label: 'Magnetic Medium Used',
								placeholder: 'Enter Magnetic Medium Used',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								options: SURFACE_TEMPERATURE,
								name: 'surfaceTemperature',
								label: 'Surface Temperature',
								placeholder: 'Enter Surface Temperature',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'blackInkBatchNumber',
								label: 'Black Ink Batch Number',
								placeholder: 'Enter Black Ink Batch Number',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'blackInkExpiryDate',
								label: 'Black Ink Expiry Date',
								placeholderText: 'Select Black Ink Expiry Date',
								minDate: dayjs(this.state.data.dateOfInspection).toDate()
							})}
						</div>
						
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'whiteContrastBatchNumber',
								label: 'White Contrast Batch Number',
								placeholder: 'Enter White Contrast Batch Number',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'whiteContrastExpiryDate',
								label: 'White Contrast Expiry Date',
								placeholderText: 'Select White Contrast Expiry Date',
								minDate: dayjs(this.state.data.dateOfInspection).toDate()
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								options: getAffirmativeOptions(),
								name: 'pieGuageUsed',
								label: 'Pie Guage Used',
								placeholder: 'Select an Option'
							})}
						</div>
						
						<div className="col">
							{this.renderSelect({
								options: FORCE_CHECK_OPTION,
								name: 'liftingForceCheck',
								label: 'Lifting Force Check',
								placeholder: 'Select an Option'
							})}
						</div>

						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: getLPTModalData(),
								name: 'modal',
								placeholder: 'Enter Lux Meter Model',
								label: 'Lux Meter Model',
								isMulti: false,
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'serialNumber',
								label: 'Lux Meter Serial Number',
								placeholder: 'Enter Lux Meter Serial Number',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								disabled: !!this.getCurrentUsedFromOptions(),
								type: 'text',
								name: 'currentUsed',
								label: 'Current Used',
								placeholder: 'Enter Current Used',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: TECHNIQUES,
								name: 'technique',
								label: 'Technique',
								placeholder: 'Enter Technique',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: MATERIAL_OPTIONS,
								name: 'material',
								label: 'Material',
								placeholder: 'Enter Material',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					{this.renderButton('Submit Overview')}
				</form>
			</React.Fragment>
		);
	}
}

const EQUIPMENT_OPTIONS = [
	{
		id: 'AC Yoke',
		name: 'AC Yoke',
		currentUsed: 'AC Current'
	},
	{
		id: 'DC Yoke',
		name: 'DC Yoke',
		currentUsed: 'DC Current'
	},
	{
		id: 'Permanent Magnet',
		name: 'Permanent Magnet',
		currentUsed: 'N/A'
	}
];

const FORCE_CHECK_OPTION = [
	{
		id: 'AC-Yoke 4.5kg',
		name: 'AC-Yoke 4.5kg'
	},
	{
		id: 'Permanent Magnet 18.1kg',
		name: 'Permanent Magnet 18.1kg'
	}
];

export const SURFACE_CONDITIONS = [
	{
		id: 'Cleaned',
		name: 'Cleaned',
	},
	{
		id: 'Not Cleaned',
		name: 'Not Cleaned',
	},
	{
		id: 'As Welded',
		name: 'As Welded',
	},
	{
		id: 'Rough Surface',
		name: 'Rough Surface',
	},
];

export const SURFACE_TEMPERATURE = [
	{
		id: 'Cold',
		name: 'Cold',
	},
	{
		id: 'Hot',
		name: 'Hot',
	},
	{
		id: 'Ambient',
		name: 'Ambient',
	},
];

export const TECHNIQUES = [
	{
		id: 'Longitudinal',
		name: 'Longitudinal',
	},
	{
		id: 'Circular',
		name: 'Circular',
	},
	{
		id: 'Prod',
		name: 'Prod',
	},
	{
		id: 'Coil Central Conductor',
		name: 'Coil Central Conductor',
	},
	{
		id: 'Current Induction',
		name: 'Current Induction',
	},
	{
		id: 'Circular',
		name: 'Circular',
	},
	{
		id: 'Radial',
		name: 'Radial',
	},
];

export default MPIReportForm;
